/**
 * OccupationalExam Form Component with Stepper
 * Multi-step wizard form for creating and editing Occupational Exam catalog data
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller, type FieldErrors } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Typography,
  FormGroup,
  Divider,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
} from '@mui/icons-material';
import {
  OccupationalExamFormData,
  OccupationalExamStatus,
  OccupationalExamType,
  OccupationalExamFormProps,
} from '../../../types/occupationalExam';
import { RouteActionButton } from '../../../components/common';
import { pode } from '../../../authorization/pode';
import { PRAZOS_PADRAO_DO_EXAME_CLINICO, type CampoDePrazoDoExame } from '../prazosPadrao';

/** Formata um código TUSS em XX.XX.XX.XXX-X a partir dos dígitos digitados (máx. 10). */
const formatTussCode = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  const parts: string[] = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 6));
  if (digits.length > 6) parts.push(digits.slice(6, 9));
  let result = parts.join('.');
  if (digits.length > 9) result += `-${digits.slice(9, 10)}`;
  return result;
};

// Validation schema
const validationSchema = yup.object({
  examName: yup
    .string()
    .required('Nome do exame é obrigatório')
    .min(3, 'Mínimo de 3 caracteres')
    .max(255, 'Máximo de 255 caracteres'),
  esocialCode: yup.string().max(50, 'Máximo de 50 caracteres'),
  tussCode: yup
    .string()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .test('is-valid-tuss', 'Código TUSS inválido. Informe 10 dígitos (com ou sem pontuação, ex: 40.30.40.016-1 ou 4030400161)', (value) => {
      if (!value) return true;
      return value.replace(/\D/g, '').length === 10;
    }),
  defaultFrequency: yup.string().max(50, 'Máximo de 50 caracteres'),
  esocialDescription: yup.string().max(500, 'Máximo de 500 caracteres'),
  realizationValidity: yup.string().max(50, 'Máximo de 50 caracteres'),
  examType: yup
    .string()
    .oneOf(Object.values(OccupationalExamType), 'Informe se o exame é clínico ou complementar.')
    .required('Informe se o exame é clínico ou complementar.'),
  realizationValidityLowRiskDays: yup.number().integer('Deve ser um inteiro de dias').positive('Deve ser maior que zero').optional(),
  realizationValidityHighRiskDays: yup.number().integer('Deve ser um inteiro de dias').positive('Deve ser maior que zero').optional(),
  tussDescription: yup.string().max(500, 'Máximo de 500 caracteres'),
  examProcedure: yup.string().max(500, 'Máximo de 500 caracteres'),
  deliveryTime: yup.string().max(50, 'Máximo de 50 caracteres'),
  examConduct: yup.string(),
  criteria: yup.string(),
  examPreparation: yup.string(),
  bmiDescription: yup.string(),
  collectionMoment: yup.string().trim().max(100, 'Máximo de 100 caracteres'),
  ibeEeValue: yup.string().trim().max(100, 'Máximo de 100 caracteres'),
  annexIObservation: yup.string().trim().max(100, 'Máximo de 100 caracteres'),
  admissionDeadline: yup.string().trim().max(255, 'Use no máximo 255 caracteres.'),
  returnToWorkDeadline: yup.string().trim().max(255, 'Use no máximo 255 caracteres.'),
  riskChangeDeadline: yup.string().trim().max(255, 'Use no máximo 255 caracteres.'),
  dismissalDeadline: yup.string().trim().max(255, 'Use no máximo 255 caracteres.'),

  // Exam type flags (at least one required)
  preEmployment: yup.boolean().required(),
  periodic: yup.boolean().required(),
  dismissal: yup.boolean().required(),
  complementary: yup.boolean().required(),
  returnToWork: yup.boolean().required(),
  riskChange: yup.boolean().required(),
  clinicalConsultation: yup.boolean().required(),

  // Work type flags
  foodIndustry: yup.boolean().required(),
  electrical: yup.boolean().required(),
  pta: yup.boolean().required(),
  confinedSpace: yup.boolean().required(),
  heightWork: yup.boolean().required(),
  forklift: yup.boolean().required(),
  selfPropelledMachine: yup.boolean().required(),
  hotWork: yup.boolean().required(),

  // Report flags
  eSocial: yup.boolean().required(),
  PPP: yup.boolean().required(),
  ASO: yup.boolean().required(),
  PPR: yup.boolean().required(),
  PCMSO: yup.boolean().required(),
  analyticalReport: yup.boolean().required(),
  PCA: yup.boolean().required(),

  // Metadata
  status: yup.string().oneOf(Object.values(OccupationalExamStatus)).required(),
}).test(
  'at-least-one-exam-type',
  'Pelo menos um tipo de exame deve ser selecionado',
  function (values) {
    const hasAtLeastOne =
      values.preEmployment ||
      values.periodic ||
      values.dismissal ||
      values.complementary ||
      values.returnToWork ||
      values.riskChange ||
      values.clinicalConsultation;

    if (!hasAtLeastOne) {
      return this.createError({
        path: 'preEmployment',
        message: 'Pelo menos um tipo de exame deve ser selecionado',
      });
    }
    return true;
  }
);

// O prazo de cada motivo é validado no passo mesmo com a caixa desmarcada: escondido, o erro
// aparece na linha do motivo.
const PRAZO_DO_MOTIVO = {
  preEmployment: 'admissionDeadline',
  returnToWork: 'returnToWorkDeadline',
  riskChange: 'riskChangeDeadline',
  dismissal: 'dismissalDeadline',
} as const satisfies Partial<Record<keyof OccupationalExamFormData, CampoDePrazoDoExame>>;

type MotivoComPrazo = keyof typeof PRAZO_DO_MOTIVO;

// Define steps for the wizard
const steps = [
  'Dados Básicos',
  'Tipos de Exame',
  'Aplicabilidades',
  'Inclusões em Relatórios'
];

export const OccupationalExamForm: React.FC<OccupationalExamFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = Boolean(initialData?.id);
  const [activeStep, setActiveStep] = useState(0);
  const [campoParaFocar, setCampoParaFocar] = useState<keyof OccupationalExamFormData | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    watch,
    setValue,
    setFocus,
  } = useForm<OccupationalExamFormData>({
    resolver: yupResolver(validationSchema as yup.ObjectSchema<OccupationalExamFormData>),
    shouldUnregister: false, // Keep values when fields are unmounted (step changes)
    defaultValues: {
      examName: initialData?.examName || '',
      esocialCode: initialData?.esocialCode ?? undefined,
      tussCode: initialData?.tussCode ? formatTussCode(initialData.tussCode) : undefined,
      defaultFrequency: initialData?.defaultFrequency ?? undefined,
      esocialDescription: initialData?.esocialDescription ?? undefined,
      realizationValidity: initialData?.realizationValidity ?? undefined,
      examType: initialData?.examType ?? undefined,
      realizationValidityLowRiskDays: initialData?.realizationValidityLowRiskDays ?? undefined,
      realizationValidityHighRiskDays: initialData?.realizationValidityHighRiskDays ?? undefined,
      tussDescription: initialData?.tussDescription ?? undefined,
      examProcedure: initialData?.examProcedure ?? undefined,
      deliveryTime: initialData?.deliveryTime ?? undefined,
      examConduct: initialData?.examConduct ?? undefined,
      criteria: initialData?.criteria ?? undefined,
      examPreparation: initialData?.examPreparation ?? undefined,
      bmiDescription: initialData?.bmiDescription ?? undefined,
      // Começam como texto vazio (não undefined): apagar o campo na edição precisa chegar ao
      // backend como "" para limpar o valor, e undefined some do corpo da requisição.
      collectionMoment: initialData?.collectionMoment ?? '',
      ibeEeValue: initialData?.ibeEeValue ?? '',
      annexIObservation: initialData?.annexIObservation ?? '',
      admissionDeadline: initialData?.admissionDeadline ?? '',
      returnToWorkDeadline: initialData?.returnToWorkDeadline ?? '',
      riskChangeDeadline: initialData?.riskChangeDeadline ?? '',
      dismissalDeadline: initialData?.dismissalDeadline ?? '',

      // Exam type flags
      preEmployment: initialData?.preEmployment || false,
      periodic: initialData?.periodic || false,
      dismissal: initialData?.dismissal || false,
      complementary: initialData?.complementary || false,
      returnToWork: initialData?.returnToWork || false,
      riskChange: initialData?.riskChange || false,
      clinicalConsultation: initialData?.clinicalConsultation || false,

      // Work type flags
      foodIndustry: initialData?.foodIndustry || false,
      electrical: initialData?.electrical || false,
      pta: initialData?.pta || false,
      confinedSpace: initialData?.confinedSpace || false,
      heightWork: initialData?.heightWork || false,
      forklift: initialData?.forklift || false,
      selfPropelledMachine: initialData?.selfPropelledMachine || false,
      hotWork: initialData?.hotWork || false,

      // Report flags
      eSocial: initialData?.eSocial || false,
      PPP: initialData?.PPP || false,
      ASO: initialData?.ASO || false,
      PPR: initialData?.PPR || false,
      PCMSO: initialData?.PCMSO || false,
      analyticalReport: initialData?.analyticalReport || false,
      PCA: initialData?.PCA || false,

      // Metadata
      status: initialData?.status || OccupationalExamStatus.ACTIVE,
    },
  });

  // Detecção: código eSocial 0295 (Avaliação Clínica Ocupacional — Tabela 27) sugere Tipo OCUPACIONAL
  const esocialCodeValue = watch('esocialCode');
  const examTypeValue = watch('examType');

  // Exame novo que passa a ser clínico recebe os prazos da NR-7 nos campos ainda vazios. Só na
  // criação: na edição o prazo vazio é escolha de quem cadastrou, e preencher mudaria o quadro.
  const preencherPrazosPadraoVazios = useCallback(() => {
    if (isEditing) {
      return;
    }
    for (const campo of Object.keys(PRAZOS_PADRAO_DO_EXAME_CLINICO) as CampoDePrazoDoExame[]) {
      if (!getValues(campo)) {
        setValue(campo, PRAZOS_PADRAO_DO_EXAME_CLINICO[campo]);
      }
    }
  }, [isEditing, getValues, setValue]);

  useEffect(() => {
    const digits = (esocialCodeValue ?? '').replace(/\D/g, '');
    // eSocial 0295 (Avaliação Clínica Ocupacional) — normaliza zeros à esquerda
    if (digits !== '' && Number(digits) === 295 && !examTypeValue) {
      setValue('examType', OccupationalExamType.OCUPACIONAL);
      preencherPrazosPadraoVazios();
    }
  }, [esocialCodeValue, examTypeValue, setValue, preencherPrazosPadraoVazios]);

  // Step navigation handlers with validation
  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    console.log('🔍 [Form] === NEXT BUTTON CLICKED ===');
    console.log('🔍 [Form] Event:', e);
    console.log('🔍 [Form] Current step:', activeStep);
    console.log('🔍 [Form] Total steps:', steps.length);
    console.log('🔍 [Form] Current form values:', getValues());

    // Prevent default form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 [Form] Prevented default event');
    }

    const fieldsToValidate = getFieldsForStep(activeStep);
    console.log('🔍 [Form] Fields to validate:', fieldsToValidate);

    const isValid = await trigger(fieldsToValidate);
    console.log('🔍 [Form] Validation result:', isValid);

    if (!isValid) {
      console.error('❌ [Form] Validation failed, errors:', errors);
      console.error('❌ [Form] Current values for failed fields:',
        fieldsToValidate.reduce((acc, field) => {
          acc[field] = getValues(field);
          return acc;
        }, {} as Record<string, unknown>)
      );
    } else {
      console.log('✅ [Form] Validation passed, moving to step', activeStep + 1);
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    console.log('🔙 [Form] Going back from step', activeStep, 'to step', activeStep - 1);
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Wrap onSubmit to add logging
  const handleFormSubmit = (data: OccupationalExamFormData) => {
    console.log('📝 [Form] === FORM SUBMISSION STARTED ===');
    console.log('📝 [Form] Active step:', activeStep);
    console.log('📝 [Form] Is editing:', isEditing);
    console.log('📝 [Form] Form data received:', data);
    console.log('📝 [Form] All form values:', getValues());
    console.log('📝 [Form] Form errors:', errors);
    console.log('📝 [Form] Has errors:', Object.keys(errors).length > 0);
    console.log('📝 [Form] Calling parent onSubmit...');
    try {
      onSubmit(data);
      console.log('✅ [Form] Parent onSubmit called successfully');
    } catch (error) {
      console.error('❌ [Form] Error in onSubmit:', error);
    }
  };

  // Envio com erro: vai ao passo do primeiro campo inválido e, depois que o passo aparece, põe o
  // cursor nele. Campo escondido não recebe o cursor, mas o passo mostra o erro dele.
  const irAoPrimeiroCampoInvalido = (erros: FieldErrors<OccupationalExamFormData>) => {
    for (let passo = 0; passo < steps.length; passo++) {
      const campoInvalido = getFieldsForStep(passo).find((campo) => erros[campo]);
      if (campoInvalido) {
        setActiveStep(passo);
        setCampoParaFocar(campoInvalido);
        return;
      }
    }
  };

  useEffect(() => {
    if (campoParaFocar) {
      setFocus(campoParaFocar);
      setCampoParaFocar(null);
    }
  }, [campoParaFocar, setFocus]);

  // Get fields to validate for each step
  const getFieldsForStep = (step: number): Array<keyof OccupationalExamFormData> => {
    switch (step) {
      case 0:
        return [
          'examName',
          'esocialCode',
          'tussCode',
          'defaultFrequency',
          'esocialDescription',
          'realizationValidity',
          'realizationValidityLowRiskDays',
          'realizationValidityHighRiskDays',
          'tussDescription',
          'examProcedure',
          'deliveryTime',
          'collectionMoment',
          'ibeEeValue',
          'annexIObservation',
          'examType',
          'status'
        ];
      case 1:
        return [
          'preEmployment',
          'periodic',
          'dismissal',
          'complementary',
          'returnToWork',
          'riskChange',
          'clinicalConsultation',
          ...Object.values(PRAZO_DO_MOTIVO),
        ];
      case 2:
        return [
          'foodIndustry',
          'electrical',
          'pta',
          'confinedSpace',
          'heightWork',
          'forklift',
          'selfPropelledMachine',
          'hotWork'
        ];
      case 3:
        return [
          'eSocial',
          'PPP',
          'ASO',
          'PPR',
          'PCMSO',
          'analyticalReport',
          'PCA'
        ];
      default:
        return [];
    }
  };

  // A validade do outro tipo de exame fica guardada, escondida; se ela é que trava o passo, o aviso
  // diz qual tipo escolher para corrigi-la.
  const erroDaValidadePorGrauDeRisco =
    errors.realizationValidityLowRiskDays ?? errors.realizationValidityHighRiskDays;
  const erroDaValidadeEscondida =
    examTypeValue === OccupationalExamType.OCUPACIONAL
      ? errors.realizationValidity &&
        `A validade da realização guardada não pode ser salva (${errors.realizationValidity.message}). Escolha "Complementar" no Tipo de Exame para corrigir.`
      : erroDaValidadePorGrauDeRisco &&
        `A validade por grau de risco guardada não pode ser salva (${erroDaValidadePorGrauDeRisco.message}). Escolha "Ocupacional (exame clínico)" no Tipo de Exame para corrigir.`;

  // Step 1: Basic Information
  const renderBasicInformation = () => (
    <Grid container spacing={3}>
      {/* Exam Name */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="examName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Nome do Exame"
              fullWidth
              required
              error={Boolean(errors.examName)}
              helperText={errors.examName?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* eSocial Code */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="esocialCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Código eSocial"
              fullWidth
              error={Boolean(errors.esocialCode)}
              helperText={errors.esocialCode?.message || 'Código do exame na tabela eSocial'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* TUSS Code */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="tussCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(formatTussCode(e.target.value))}
              label="Código TUSS"
              fullWidth
              error={Boolean(errors.tussCode)}
              helperText={errors.tussCode?.message || 'Formato: XX.XX.XX.XXX-X (ex: 40.30.40.016-1)'}
              disabled={loading}
              placeholder="40.30.40.016-1"
              inputProps={{ maxLength: 14 }}
            />
          )}
        />
      </Grid>

      {/* Default Frequency */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="defaultFrequency"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Periodicidade Padrão"
              fullWidth
              error={Boolean(errors.defaultFrequency)}
              helperText={errors.defaultFrequency?.message || 'Ex: Anual, Semestral, 12 meses'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* eSocial Description */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="esocialDescription"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Descrição eSocial (S-2220)"
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.esocialDescription)}
              helperText={errors.esocialDescription?.message || 'Descrição conforme tabela eSocial S-2220'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Anexo I da NR-7: momento da coleta */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="collectionMoment"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Momento da Coleta"
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.collectionMoment)}
              helperText={errors.collectionMoment?.message || 'Sigla do Anexo I da NR-7 (ex.: FJ – final da jornada)'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Tipo de Exame (classificação: Ocupacional / Complementar) */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <FormControl fullWidth required error={Boolean(errors.examType)}>
          <InputLabel id="examType-label">Tipo de Exame</InputLabel>
          <Controller
            name="examType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? ''}
                onChange={(event) => {
                  field.onChange(event);
                  if (event.target.value === OccupationalExamType.OCUPACIONAL) {
                    preencherPrazosPadraoVazios();
                  }
                }}
                labelId="examType-label"
                label="Tipo de Exame"
                disabled={loading}
              >
                <MenuItem value={OccupationalExamType.OCUPACIONAL}>Ocupacional (exame clínico)</MenuItem>
                <MenuItem value={OccupationalExamType.COMPLEMENTAR}>Complementar</MenuItem>
              </Select>
            )}
          />
          <FormHelperText>
            {errors.examType?.message ??
              'Clínico entra na tabela de exames clínicos do Relatório Analítico do PCMSO; complementar, na de exames complementares. Código eSocial 0295 já sugere clínico.'}
          </FormHelperText>
        </FormControl>
      </Grid>

      {/* Validade da Realização — por grau de risco (Ocupacional) ou validade única (Complementar) */}
      {examTypeValue === OccupationalExamType.OCUPACIONAL ? (
        <>
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name="realizationValidityLowRiskDays"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  label="Validade — Grau de risco 1-2"
                  type="number"
                  fullWidth
                  error={Boolean(errors.realizationValidityLowRiskDays)}
                  helperText={errors.realizationValidityLowRiskDays?.message || 'Ex: 135'}
                  disabled={loading}
                  InputProps={{ endAdornment: <InputAdornment position="end">dias</InputAdornment> }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name="realizationValidityHighRiskDays"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  label="Validade — Grau de risco 3-4"
                  type="number"
                  fullWidth
                  error={Boolean(errors.realizationValidityHighRiskDays)}
                  helperText={errors.realizationValidityHighRiskDays?.message || 'Ex: 90'}
                  disabled={loading}
                  InputProps={{ endAdornment: <InputAdornment position="end">dias</InputAdornment> }}
                />
              )}
            />
          </Grid>
        </>
      ) : (
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="realizationValidity"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Validade da Realização"
                fullWidth
                error={Boolean(errors.realizationValidity)}
                helperText={errors.realizationValidity?.message || 'Ex: 90 dias, 6 meses, 1 ano'}
                disabled={loading}
              />
            )}
          />
        </Grid>
      )}
      {erroDaValidadeEscondida && (
        <Grid size={{ xs: 12 }}>
          <FormHelperText error>{erroDaValidadeEscondida}</FormHelperText>
        </Grid>
      )}

      {/* Delivery Time */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="deliveryTime"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Prazo de Entrega"
              fullWidth
              error={Boolean(errors.deliveryTime)}
              helperText={errors.deliveryTime?.message || 'Ex: 24h, 48h, 5 dias úteis'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Anexo I da NR-7: valor do IBE/EE, ao lado do prazo e acima da observação */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="ibeEeValue"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Valor do IBE/EE"
              fullWidth
              error={Boolean(errors.ibeEeValue)}
              helperText={errors.ibeEeValue?.message || 'Ex.: 25 mg/L'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* TUSS Description */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="tussDescription"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Descrição TUSS"
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.tussDescription)}
              helperText={errors.tussDescription?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Anexo I da NR-7: observação */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Controller
          name="annexIObservation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Observação – Anexo I"
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.annexIObservation)}
              helperText={errors.annexIObservation?.message || 'Sigla de observação do Anexo I da NR-7 (ex.: NE)'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Exam Procedure */}
      <Grid size={12}>
        <Controller
          name="examProcedure"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Procedimento do Exame"
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors.examProcedure)}
              helperText={errors.examProcedure?.message || 'Descreva como o exame é realizado'}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Criteria */}
      <Grid size={12}>
        <Controller
          name="criteria"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Critérios de Interpretação dos Resultados"
              fullWidth
              multiline
              rows={3}
              error={Boolean(errors.criteria)}
              helperText={errors.criteria?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>

      {/* Status */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <FormControl fullWidth required>
          <InputLabel>Status</InputLabel>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select {...field} label="Status" disabled={loading}>
                <MenuItem value={OccupationalExamStatus.ACTIVE}>Ativo</MenuItem>
                <MenuItem value={OccupationalExamStatus.INACTIVE}>Inativo</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
  );

  // Step 2: Exam Types
  type MotivoDoExame = 'preEmployment' | 'periodic' | 'returnToWork' | 'riskChange' | 'dismissal';

  const renderCaixaDeMotivo = (
    name: MotivoDoExame | 'complementary' | 'clinicalConsultation',
    label: string
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={<Checkbox {...field} checked={field.value} disabled={loading} />}
          label={label}
        />
      )}
    />
  );

  // Marcada, a caixa mostra ao lado o que vale para aquele motivo; desmarcada, o campo some sem
  // apagar o texto (remarcar devolve o que havia) e a linha diz o que o quadro imprime.
  const renderLinhaDeMotivo = (motivo: MotivoDoExame, label: string, conteudo: React.ReactNode) => {
    const erroDoPrazoGuardado = motivo === 'periodic' ? undefined : errors[PRAZO_DO_MOTIVO[motivo]]?.message;
    return (
      <Grid container spacing={2} sx={{ py: 1.5, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
        <Grid size={{ xs: 12, md: 4 }}>{renderCaixaDeMotivo(motivo, label)}</Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          {watch(motivo) ? (
            conteudo
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, display: 'flex', alignItems: 'center' }}>
              Motivo não marcado: o quadro imprime "—".
            </Typography>
          )}
          {!watch(motivo) && erroDoPrazoGuardado && (
            <FormHelperText error>
              O prazo guardado para este motivo não pode ser salvo: {erroDoPrazoGuardado} Marque o motivo para corrigir o texto.
            </FormHelperText>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderCampoDePrazo = (name: CampoDePrazoDoExame, label: string, ajuda?: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field } }) => (
        <TextField
          {...field}
          inputRef={ref}
          value={field.value ?? ''}
          label={label}
          fullWidth
          error={Boolean(errors[name])}
          helperText={errors[name]?.message ?? ajuda}
          disabled={loading}
        />
      )}
    />
  );

  const renderLinhaComPrazo = (motivo: MotivoComPrazo, label: string, labelDoPrazo: string, ajuda?: string) =>
    renderLinhaDeMotivo(motivo, label, renderCampoDePrazo(PRAZO_DO_MOTIVO[motivo], labelDoPrazo, ajuda));

  const renderExamTypes = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
        Tipos de Exame (selecione pelo menos um)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Marque em quais motivos este exame é feito e diga o prazo de cada um. O prazo é o texto que
        sai no quadro de exames do PCMSO.
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {errors.preEmployment && (
        <FormHelperText error sx={{ mb: 2 }}>
          {errors.preEmployment.message}
        </FormHelperText>
      )}
      <FormGroup>
        <Box>
          {renderLinhaComPrazo(
            'preEmployment',
            'Admissional',
            'Prazo na admissão',
            'Ex.: "Antes de assumir as atividades". Vazio, o quadro imprime "Sim".'
          )}
          {renderLinhaDeMotivo(
            'periodic',
            'Periódico',
            <Alert severity="info">
              O prazo do periódico é a periodicidade que você define ao aplicar o exame a cada cargo,
              setor ou empresa. Ele pode ser diferente em cada cargo.{' '}
              <RouteActionButton
                para="/exam-applications"
                exige={pode('exam_application.read', { perspectiva: 'plataforma' })}
                aoAgir={() => window.open('/exam-applications', '_blank', 'noopener,noreferrer')}
                size="small"
                color="inherit"
                sx={{ fontWeight: 600, textTransform: 'none', verticalAlign: 'baseline', p: 0, minWidth: 0 }}
              >
                Ver aplicações de exames
              </RouteActionButton>
            </Alert>
          )}
          {renderLinhaComPrazo('returnToWork', 'Retorno ao Trabalho', 'Prazo no retorno ao trabalho')}
          {renderLinhaComPrazo('riskChange', 'Mudança de Risco', 'Prazo na mudança de risco')}
          {renderLinhaComPrazo('dismissal', 'Demissional', 'Prazo na demissão')}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, pt: 2, mt: 1, borderTop: 1, borderColor: 'divider' }}>
          {renderCaixaDeMotivo('complementary', 'Complementar')}
          {renderCaixaDeMotivo('clinicalConsultation', 'Consulta Clínica')}
        </Box>
      </FormGroup>
    </Box>
  );

  // Step 3: Work Type Applicabilities
  const renderWorkTypes = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
        Aplicabilidades (tipos de trabalho)
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <FormGroup>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="foodIndustry"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Indústria Alimentícia"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="electrical"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Eletricidade (NR-10)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="pta"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="PTA (Programa de Proteção Auditiva)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="confinedSpace"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Espaço Confinado (NR-33)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="heightWork"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Trabalho em Altura (NR-35)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="forklift"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Empilhadeira"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="selfPropelledMachine"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Máquina Autopropelida"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="hotWork"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Trabalho a Quente"
                />
              )}
            />
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );

  // Step 4: Report Inclusions
  const renderReportInclusions = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
        Inclusões em Relatórios
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <FormGroup>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="eSocial"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="eSocial (S-2220)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="PPP"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="PPP (Perfil Profissiográfico Previdenciário)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="ASO"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="ASO (Atestado de Saúde Ocupacional)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="PPR"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="PPR"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="PCMSO"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="PCMSO (Programa de Controle Médico de Saúde Ocupacional)"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="analyticalReport"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="Relatório Analítico"
                />
              )}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="PCA"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} disabled={loading} />}
                  label="PCA (Programa de Conservação Auditiva)"
                />
              )}
            />
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit, irAoPrimeiroCampoInvalido)} noValidate>
      {/* Stepper */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content - Render all steps but hide inactive ones */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
          {renderBasicInformation()}
        </Box>
        <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
          {renderExamTypes()}
        </Box>
        <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
          {renderWorkTypes()}
        </Box>
        <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
          {renderReportInclusions()}
        </Box>
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="button"
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep > 0 && (
            <Button
              type="button"
              onClick={handleBack}
              variant="outlined"
              startIcon={<BackIcon />}
              disabled={loading}
            >
              Voltar
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              variant="contained"
              endIcon={<NextIcon />}
              disabled={loading}
            >
              Próximo
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
              onClick={() => {
                console.log('🖱️ [Form] Submit button clicked!');
                console.log('🖱️ [Form] Current step:', activeStep);
                console.log('🖱️ [Form] Is submitting:', loading);
                console.log('🖱️ [Form] Current form values:', getValues());
                console.log('🖱️ [Form] Form errors:', errors);
              }}
            >
              {isEditing ? 'Atualizar' : 'Criar'} Exame
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OccupationalExamForm;
